'use client';

import { useRef, useState, useEffect } from 'react';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Icon from '@/components/ui/AppIcon';
import MobileWhyChooseUs from './MobileWhyChooseUs';

if (typeof window !== 'undefined') {
    gsap.registerPlugin(ScrollTrigger);
}

// Spotlight Card Component
const SpotlightCard = ({ children, className = '', spotlightColor = 'rgba(99, 102, 241, 0.15)' }: any) => {
    const divRef = useRef<HTMLDivElement>(null);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [opacity, setOpacity] = useState(0);

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!divRef.current) return;
        const rect = divRef.current.getBoundingClientRect();
        setPosition({ x: e.clientX - rect.left, y: e.clientY - rect.top });
        setOpacity(1);
    };

    const handleMouseLeave = () => {
        setOpacity(0);
    };

    return (
        <div
            ref={divRef}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            className={`relative overflow-hidden rounded-3xl border border-slate-800 bg-slate-900/50 backdrop-blur-xl p-8 ${className}`}
        >
            {/* Spotlight Gradient */}
            <div
                className="pointer-events-none absolute -inset-px opacity-0 transition-opacity duration-300"
                style={{
                    opacity,
                    background: `radial-gradient(600px circle at ${position.x}px ${position.y}px, ${spotlightColor}, transparent 40%)`,
                }}
            />
            {/* Content */}
            <div className="relative z-10 h-full">{children}</div>
        </div>
    );
};

import ScheduleModal from '@/components/ScheduleModal';

const WhyChooseUs = () => {
    const containerRef = useRef<HTMLElement>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    useGSAP(() => {
        const tl = gsap.timeline({
            scrollTrigger: {
                trigger: containerRef.current,
                start: 'top 60%',
            }
        });

        tl.from('.bento-item', {
            y: 50,
            opacity: 0,
            duration: 0.8,
            stagger: 0.1,
            ease: 'power3.out'
        });

        // Floating Crystals Animation
        gsap.to('.crystal-float', {
            y: -50,
            rotation: 360,
            duration: 20,
            repeat: -1,
            yoyo: true,
            ease: 'sine.inOut'
        });

    }, { scope: containerRef });

    return (
        <>
            <section ref={containerRef} className="hidden lg:block py-32 bg-slate-950 relative overflow-hidden">
                {/* Background 3D Elements (Abstract Crystals) */}
                <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-indigo-500/10 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/2" />
                    <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-fuchsia-500/10 rounded-full blur-[100px] translate-y-1/2 -translate-x-1/2" />

                    {/* Floating Shapes */}
                    <div className="crystal-float absolute top-[20%] right-[10%] w-24 h-24 border border-indigo-500/20 rounded-xl rotate-45 backdrop-blur-sm" />
                    <div className="crystal-float absolute bottom-[30%] left-[5%] w-32 h-32 border border-fuchsia-500/20 rounded-full backdrop-blur-sm" style={{ rx: '100px', animationDelay: '-5s' }} />
                </div>

                <div className="container mx-auto px-4 relative z-10">
                    <div className="text-center mb-20">
                        <h2 className="text-4xl md:text-6xl font-black text-white mb-6">
                            Why We Are The <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-cyan-400">Anomalies</span>
                        </h2>
                        <p className="text-slate-400 text-lg max-w-2xl mx-auto">
                            We don't just build websites; we engineer digital dominance. Here is what sets our DNA apart.
                        </p>
                    </div>

                    {/* Bento Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6 max-w-7xl mx-auto auto-rows-[minmax(180px,auto)]">

                        {/* Item 1: Large Feature (Span 2x2) */}
                        <SpotlightCard className="bento-item md:col-span-2 md:row-span-2 flex flex-col justify-between group">
                            <div>
                                <div className="w-14 h-14 rounded-2xl bg-indigo-500/20 flex items-center justify-center text-indigo-400 mb-6 group-hover:scale-110 transition-transform duration-300">
                                    <Icon name="RocketLaunchIcon" className="w-8 h-8" />
                                </div>
                                <h3 className="text-3xl font-bold text-white mb-4">Speed & Performance First</h3>
                                <p className="text-slate-400 leading-relaxed">
                                    We obsess over milliseconds. Our architectures are optimized for perfect Core Web Vitals, ensuring your site ranks higher and converts better. No bloat, just raw speed.
                                </p>
                            </div>
                            <div className="mt-8 relative h-32 w-full bg-indigo-900/20 rounded-xl overflow-hidden border border-indigo-500/10">
                                {/* Animated Graph Line */}
                                <div className="absolute inset-0 flex items-end">
                                    <div className="w-full h-full bg-gradient-to-t from-indigo-500/20 to-transparent skew-y-6 origin-bottom-left" />
                                </div>
                            </div>
                        </SpotlightCard>

                        {/* Item 2: Stat Card */}
                        <SpotlightCard className="bento-item flex flex-col items-center justify-center text-center">
                            <span className="text-5xl font-black text-transparent bg-clip-text bg-gradient-to-br from-indigo-400 to-fuchsia-400 mb-2">100%</span>
                            <span className="font-bold text-slate-300">Ownership</span>
                            <span className="text-xs text-slate-500 mt-1">No vendor lock-in</span>
                        </SpotlightCard>

                        {/* Item 3: Feature (SEO) */}
                        <SpotlightCard className="bento-item">
                            <Icon name="ChartBarIcon" className="w-8 h-8 text-fuchsia-400 mb-4" />
                            <h4 className="text-xl font-bold text-white mb-2">SEO Dominance</h4>
                            <p className="text-sm text-slate-400">Technical SEO built-in. We structure your site to be loved by search engines from day one.</p>
                        </SpotlightCard>

                        {/* Item 4: Feature (Wide) */}
                        <SpotlightCard className="bento-item md:col-span-2">
                            <div className="flex items-start gap-6">
                                <div className="w-12 h-12 rounded-xl bg-cyan-500/20 flex items-center justify-center text-cyan-400 flex-shrink-0">
                                    <Icon name="CodeBracketSquareIcon" className="w-6 h-6" />
                                </div>
                                <div>
                                    <h4 className="text-xl font-bold text-white mb-2">Clean, Modern Code</h4>
                                    <p className="text-slate-400">
                                        We write maintainable, type-safe code using the latest standards.
                                        React, Next.js, and TypeScript are our native tongue.
                                    </p>
                                </div>
                            </div>
                        </SpotlightCard>

                        {/* Item 5: Feature (GMB) */}
                        <SpotlightCard className="bento-item">
                            <Icon name="MapIcon" className="w-8 h-8 text-emerald-400 mb-4" />
                            <h4 className="text-xl font-bold text-white mb-2">Google Profile Mastery</h4>
                            <p className="text-sm text-slate-400">We optimize your GMB profile to dominate local search results and drive foot traffic.</p>
                        </SpotlightCard>

                        <SpotlightCard className="bento-item md:col-span-2 md:row-span-1 bg-gradient-to-r from-indigo-600 to-violet-600 border-none">
                            <div className="flex flex-col md:flex-row items-center justify-between gap-6 h-full">
                                <div>
                                    <h3 className="text-2xl font-black text-white mb-2">Ready to Scale?</h3>
                                    <p className="text-indigo-100">Let's build something extraordinary together.</p>
                                </div>
                                <button
                                    onClick={() => setIsModalOpen(true)}
                                    className="px-8 py-3 bg-white text-indigo-700 font-bold rounded-xl hover:scale-105 transition-transform shadow-xl"
                                >
                                    Start Now
                                </button>
                            </div>
                        </SpotlightCard>

                    </div>
                </div>
            </section>

            <div className="block lg:hidden">
                <MobileWhyChooseUs />
            </div>

            <ScheduleModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
        </>
    );
};

export default WhyChooseUs;
